import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import MuiChip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';

import { styled } from '@mui/material/styles';

import StreamIcon from '@mui/icons-material/Stream';
import EdgesensorHighRoundedIcon from '@mui/icons-material/EdgesensorHighRounded';
import ChatIcon from '@mui/icons-material/Chat';

const items = [
  {
    icon: <ChatIcon />,
    title: 'Ask and you shall receive',
    description: "Ask our AI executive assistant for what you'd like to know in plain language. You can get executive summaries, then visualize and explore the raw data the assistant used to answer your question. No clumsy interfaces to learn, and no data analysists to wait around for.",
    imageLight: 'url("/natural.jpg")',
    imageDark: 'url("/natural.jpg")',
  },
  {
    icon: <EdgesensorHighRoundedIcon />,
    title: 'Take it with you everywhere',
    description: "With our mobile app, your executive AI is always there, day and night. Our unique model knows how to provide both succinct, to-the-point answers, as well as the ability to dive deeper by presenting and visualizing the backing data as slides.",
    imageLight: 'url("/mobile.jpg")',
    imageDark: 'url("/mobile.jpg")',
  },
  {
    icon: <StreamIcon />,
    title: 'Works with data you already have',
    description: "Orange creates an executuve AI out of *your* data. We integrate with over 30 well-known CRMs, marketing platforms, productivity tools, project trackers, chat/email apps, and now even directly to your spreadsheets and SQL databases! And the list keeps growing every day.",
    imageLight: 'url("/dataintegration.jpg")',
    imageDark: 'url("/dataintegration.jpg")',
  },
];

const Chip = styled(MuiChip)(({ theme }) => ({
  variants: [
    {
      props: ({ selected }) => selected,
      style: {
        background:
          'linear-gradient(to bottom right, hsl(210, 98%, 48%), hsl(210, 98%, 35%))',
        color: 'hsl(0, 0%, 100%)',
        borderColor: theme.palette.primary.light,
        '& .MuiChip-label': {
          color: 'hsl(0, 0%, 100%)',
        },
        ...theme.applyStyles('dark', {
          borderColor: theme.palette.primary.dark,
        }),
      },
    },
  ],
}));

function MobileLayout({ selectedItemIndex, handleItemClick, selectedFeature }) {
  if (!items[selectedItemIndex]) {
    return null;
  }

  return (
    <Box
      sx={{
        display: { xs: 'flex', sm: 'none' },
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', gap: 2, overflow: 'auto' }}>
        {items.map(({ title }, index) => (
          <Chip
            size="medium"
            key={index}
            label={title}
            onClick={() => handleItemClick(index)}
            selected={selectedItemIndex === index}
          />
        ))}
      </Box>
      <Card variant="outlined">
        <Box
          sx={(theme) => ({
            mb: 2,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            minHeight: 280,
            backgroundImage: 'var(--items-imageLight)',
            ...theme.applyStyles('dark', {
              backgroundImage: 'var(--items-imageDark)',
            }),
          })}
          style={
            items[selectedItemIndex]
              ? {
                  '--items-imageLight': items[selectedItemIndex].imageLight,
                  '--items-imageDark': items[selectedItemIndex].imageDark,
                }
              : {}
          }
        />
        <Box sx={{ px: 2, pb: 2 }}>
          <Typography
            gutterBottom
            sx={{ color: 'text.primary', fontWeight: 'medium' }}
          >
            {selectedFeature.title}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
            {selectedFeature.description}
          </Typography>
        </Box>
      </Card>
    </Box>
  );
}

MobileLayout.propTypes = {
  handleItemClick: PropTypes.func.isRequired,
  selectedFeature: PropTypes.shape({
    description: PropTypes.string.isRequired,
    icon: PropTypes.element,
    imageDark: PropTypes.string.isRequired,
    imageLight: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
  }).isRequired,
  selectedItemIndex: PropTypes.number.isRequired,
};

export { MobileLayout };

export default function Features() {
  const [selectedItemIndex, setSelectedItemIndex] = React.useState(0);

  const handleItemClick = (index) => {
    setSelectedItemIndex(index);
  };

  const selectedFeature = items[selectedItemIndex];

  return (
    <Container
        id="features"
        sx={{ py: { xs: 8, sm: 16 }, fontSize : '1.3rem' }}
    >
        <Box sx={{ width: { sm: '100%', md: '60%' } }}>
            <Typography
                component="h2"
                variant="h2"
                gutterBottom
                //sx={{ color: 'white' }}
            >
                Product features
            </Typography>
            <Typography
                variant="body1"
                sx={{ /*color: 'white',*/ mb: { xs: 2, sm: 4 }, fontSize : '1.3rem' }}
            >
                Just a sample of what Orange has to offer.
            </Typography>
        </Box>
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row-reverse' },
                gap: 2,
            }}
        >
            <div>
                <Box
                    sx={{
                      display: { xs: 'none', sm: 'flex' },
                      flexDirection: 'column',
                      gap: 2,
                      height: '100%',
                    }}
                >
                    {items.map(({ icon, title, description }, index) => (
                        <Box
                            key={index}
                            component={Button}
                            onClick={() => handleItemClick(index)}
                            sx={[
                              (theme) => ({
                                p: 2,
                                height: '100%',
                                width: '100%',
                                '&:hover': {
                                  backgroundColor: theme.palette.action.hover,
                                },
                              }),
                              selectedItemIndex === index && {
                                backgroundColor: 'action.selected',
                              },
                            ]}
                        >
                            <Box
                                sx={[
                                    {
                                      width: '100%',
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'left',
                                      gap: 1,
                                      textAlign: 'left',
                                      textTransform: 'none',
                                      color: 'text.secondary',
                                    },
                                    selectedItemIndex === index && {
                                      color: 'text.primary',
                                    },
                                ]}
                            >
                                {icon}
                                <Typography variant="h6">{title}</Typography>
                                <Typography variant="body2">{description}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>
                <MobileLayout
                    selectedItemIndex={selectedItemIndex}
                    handleItemClick={handleItemClick}
                    selectedFeature={selectedFeature}
                />
            </div>
            <Box
                sx={{
                    display: { xs: 'none', sm: 'flex' },
                    width: { xs: '100%', md: '70%' },
                    height: 'var(--items-image-height)',
                }}
            >
                <Card
                    variant="outlined"
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: { xs: 'none', sm: 'flex' },
                        pointerEvents: 'none',
                    }}
                >
                    <Box
                        sx={(theme) => ({
                            m: 'auto',
                            width: 420,
                            height: 500,
                            backgroundSize: 'contain',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            backgroundImage: 'var(--items-imageLight)',
                            ...theme.applyStyles('dark', {
                                backgroundImage: 'var(--items-imageDark)',
                            }),
                        })}
                        style={
                            items[selectedItemIndex]
                            ? {
                              '--items-imageLight': items[selectedItemIndex].imageLight,
                              '--items-imageDark': items[selectedItemIndex].imageDark,
                            }
                            : {}
                        }
                    />
                </Card>
            </Box>
        </Box>
    </Container>
  );
}
